import { Component, Input, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { RenderJob } from '../render-flow.model';
import { RenderFeedbackRating, RenderFeedbackService } from '../render-feedback.service';
import { RenderOrientation } from '../render.data';
import { openRenderFeedback } from './render-rating.component';

// The thumbs on one of the preview's frames (designer, Figma 28547:27273: 28721:44956 on the display,
// 28753:46102 on the phone; owner 2026-10-20, the phone too 2026-10-21): up stores a like; down opens
// the form, which may go empty, and the dislike goes with Send (Back drops it). Each frame is rated
// on its own. The chosen thumb fills and stays filled for this model and frame, on any device (the
// backend keeps it, Yahor 2026-11-05); the other one can change it, and pressing it again takes it
// back.
@Component({
  selector: 'app-render-vote',
  templateUrl: './render-vote.component.html',
  styleUrls: ['./render-vote.component.scss'],
})
export class RenderVoteComponent implements OnChanges {
  @Input() job?: RenderJob;
  @Input() orientation: RenderOrientation = 'landscape';

  public rating: RenderFeedbackRating | null = null;
  public busy = false;
  public failed = false;
  // The model the thumbs are read for: the job itself changes with every word from the renderer.
  private readFor?: string;

  constructor(
    private readonly dialog: MatDialog,
    private readonly feedback: RenderFeedbackService,
    private readonly firebaseAuth: FirebaseAuthService,
  ) {}

  ngOnChanges(): void {
    if (this.job?.modelId !== this.readFor) {
      this.readFor = this.job?.modelId;
      this.rating = null;
      this.read();
    }
  }

  public async like(): Promise<void> {
    if (!this.job || this.busy) {
      return;
    }
    await this.press(this.job, this.rating === 'like' ? null : 'like');
  }

  public dislike(): void {
    if (!this.job || this.busy) {
      return;
    }
    if (this.rating === 'dislike') {
      this.press(this.job, null);
      return;
    }
    this.failed = false;
    openRenderFeedback(this.dialog, this.firebaseAuth, { job: this.job, stage: 'preview', rating: 'dislike', orientation: this.orientation }).afterClosed().subscribe(() => this.read());
  }

  // Shown at once; back as it was if it did not go through.
  private async press(job: RenderJob, rating: 'like' | null): Promise<void> {
    const before = this.rating;
    this.busy = true;
    this.failed = false;
    this.rating = rating;
    try {
      await (rating
        ? this.feedback.send(job, { stage: 'preview', rating, improve: [], orientation: this.orientation, text: '' })
        : this.feedback.clearRating(job, this.orientation));
    } catch {
      this.rating = before;
      this.failed = true;
    } finally {
      this.busy = false;
    }
  }

  // A press made while the answer was on its way knows better.
  private async read(): Promise<void> {
    const job = this.job;
    if (!job) {
      return;
    }
    const rating = await this.feedback.rating(job, this.orientation);
    if (this.job?.modelId === job.modelId && !this.busy) {
      this.rating = rating;
    }
  }
}
